// ┌──────────────────────────────────────────────────────────────────────────┐
// │  Double-elimination engine                                                 │
// │                                                                            │
// │  The whole tournament is modeled as a graph of matches. Each match knows   │
// │  where its WINNER advances (winnerTo) and where its LOSER drops (loserTo). │
// │  Byes are resolved with a fixpoint pass that auto-advances any match where │
// │  one side can never be filled.                                             │
// │                                                                            │
// │  Slot value conventions (team1 / team2):                                   │
// │    string     → a real player                                             │
// │    null       → a resolved bye (no player will ever arrive)               │
// │    undefined  → pending (awaiting an upstream result)                      │
// └──────────────────────────────────────────────────────────────────────────┘

import { nextPow2, randomSlots, seededSlots, MH, MW, VG, HG, PAD } from "./bracketLogic";

export const CHAMP_W = 160; // champion box width (matches single-elim renderer)
const BAND_GAP = 64; // vertical gap between winners and losers bands
const LB_PITCH = 2 * (MH + VG); // vertical spacing of losers-bracket leaves

// Number of losers-bracket matches in 1-indexed LB round r for a size-S bracket.
function countLb(S, r) {
  return S / Math.pow(2, Math.ceil(r / 2) + 1);
}

// ── Build the empty structure (matches + routing + layout), no players yet ───
export function buildStructure(size) {
  const S = size;
  const k = Math.log2(S); // number of winners-bracket rounds
  const numLb = Math.max(0, 2 * k - 2); // number of losers-bracket rounds

  const matches = [];
  const byId = {};
  const add = (m) => {
    byId[m.id] = m;
    matches.push(m);
    return m;
  };
  const mk = (id, bracket, round, slot) =>
    add({
      id,
      bracket,
      round,
      slot,
      team1: undefined,
      team2: undefined,
      winner: undefined,
      loser: undefined,
      decided: false,
      winnerTo: null,
      loserTo: null,
      x: 0,
      y: 0,
    });

  // Winners bracket matches
  for (let r = 0; r < k; r++) {
    const count = S >> (r + 1);
    for (let s = 0; s < count; s++) mk(`W${r}-${s}`, "W", r, s);
  }
  // Losers bracket matches
  for (let r = 1; r <= numLb; r++) {
    const count = countLb(S, r);
    for (let s = 0; s < count; s++) mk(`L${r}-${s}`, "L", r, s);
  }
  // Grand final + reset
  mk("GF", "GF", 0, 0);
  mk("GFR", "GF", 1, 0);

  // ── Winner routing inside the winners bracket ──
  for (let r = 0; r < k - 1; r++) {
    const count = S >> (r + 1);
    for (let s = 0; s < count; s++) {
      byId[`W${r}-${s}`].winnerTo = {
        id: `W${r + 1}-${s >> 1}`,
        slot: s % 2 === 0 ? 1 : 2,
      };
    }
  }
  byId[`W${k - 1}-0`].winnerTo = { id: "GF", slot: 1 }; // WB champ → grand final

  // ── Loser routing: winners-bracket losers drop into the losers bracket ──
  for (let i = 0; i < k; i++) {
    const cnt = S >> (i + 1);
    for (let m = 0; m < cnt; m++) {
      const w = byId[`W${i}-${m}`];
      if (i === k - 1) {
        // WB final loser → losers-bracket final (or straight to GF if no LB)
        w.loserTo = numLb > 0 ? { id: `L${numLb}-0`, slot: 2 } : { id: "GF", slot: 2 };
      } else if (i === 0) {
        // WB round-1 losers fill LB round 1, two per match
        w.loserTo = { id: `L1-${m >> 1}`, slot: m % 2 === 0 ? 1 : 2 };
      } else {
        // Later WB losers drop into even LB rounds, reversed (crossover) so a
        // dropping player doesn't immediately rematch someone from their group.
        w.loserTo = { id: `L${2 * i}-${cnt - 1 - m}`, slot: 2 };
      }
    }
  }

  // ── Winner routing inside the losers bracket ──
  for (let r = 1; r < numLb; r++) {
    const count = countLb(S, r);
    for (let s = 0; s < count; s++) {
      const m = byId[`L${r}-${s}`];
      if (r % 2 === 1) {
        // odd round → next (even drop) round, 1:1 into the LB-survivor slot
        m.winnerTo = { id: `L${r + 1}-${s}`, slot: 1 };
      } else {
        // even round → next (odd internal) round, pair survivors up
        m.winnerTo = { id: `L${r + 1}-${s >> 1}`, slot: s % 2 === 0 ? 1 : 2 };
      }
    }
  }
  if (numLb > 0) byId[`L${numLb}-0`].winnerTo = { id: "GF", slot: 2 }; // LB champ → grand final

  layout({ matches, byId, size: S, k, numLb });
  return { matches, byId, size: S, k, numLb };
}

// ── Layout: position every match ─────────────────────────────────────────────
function layout(state) {
  const { byId, size: S, k, numLb } = state;

  // Winners bracket: classic binary-tree vertical centering.
  const wbY = (r, s) =>
    r === 0
      ? PAD + s * (MH + VG) + MH / 2
      : (wbY(r - 1, 2 * s) + wbY(r - 1, 2 * s + 1)) / 2;
  for (let r = 0; r < k; r++) {
    const count = S >> (r + 1);
    for (let s = 0; s < count; s++) {
      const m = byId[`W${r}-${s}`];
      m.x = PAD + r * (MW + HG);
      m.y = wbY(r, s);
    }
  }

  const wbBandHeight = (S / 2) * (MH + VG) - VG;
  const lbTop = PAD + wbBandHeight + BAND_GAP;

  // Losers bracket: leaves (round 1) spread evenly; deeper rounds centre on the
  // losers-bracket matches that feed their survivor slot.
  const winChildren = {};
  for (const m of state.matches) {
    if (m.winnerTo) (winChildren[m.winnerTo.id] ||= []).push(m);
  }
  let leafIdx = 0;
  for (let r = 1; r <= numLb; r++) {
    const count = countLb(S, r);
    for (let s = 0; s < count; s++) {
      const m = byId[`L${r}-${s}`];
      m.x = PAD + (r - 1) * (MW + HG);
      if (r === 1) {
        m.y = lbTop + leafIdx++ * LB_PITCH + MH / 2;
      } else {
        const kids = (winChildren[m.id] || []).filter((c) => c.bracket === "L");
        m.y = kids.length
          ? kids.reduce((a, c) => a + c.y, 0) / kids.length
          : lbTop + s * LB_PITCH + MH / 2;
      }
    }
  }

  // Grand final + reset, to the right of both brackets.
  const gfCol = Math.max(k - 1, numLb - 1) + 1;
  const wbFinal = byId[`W${k - 1}-0`];
  const lbFinal = numLb > 0 ? byId[`L${numLb}-0`] : null;
  const gf = byId["GF"];
  gf.x = PAD + gfCol * (MW + HG);
  gf.y = lbFinal ? (wbFinal.y + lbFinal.y) / 2 : wbFinal.y + (MH + VG);
  const gfr = byId["GFR"];
  gfr.x = PAD + (gfCol + 1) * (MW + HG);
  gfr.y = gf.y;
}

// ── Bye resolution (fixpoint) ────────────────────────────────────────────────
function setSlot(byId, ref, value) {
  const m = byId[ref.id];
  if (ref.slot === 1) m.team1 = value;
  else m.team2 = value;
}

function propagate(byId, m) {
  if (m.winnerTo) setSlot(byId, m.winnerTo, m.winner == null ? null : m.winner);
  if (m.loserTo) setSlot(byId, m.loserTo, m.loser == null ? null : m.loser);
}

function resolveByes(state) {
  const { matches, byId } = state;
  let changed = true;
  while (changed) {
    changed = false;
    for (const m of matches) {
      if (m.decided || m.bracket === "GF") continue;
      const { team1: t1, team2: t2 } = m;
      if (t1 === undefined || t2 === undefined) continue; // still pending
      const real1 = t1 !== null;
      const real2 = t2 !== null;
      if (real1 && real2) continue; // playable — wait for the user
      // At least one side is a bye → auto-resolve.
      m.decided = true;
      m.winner = real1 ? t1 : real2 ? t2 : null;
      m.loser = null; // a bye/empty match has no real loser
      m.isBye = true;
      propagate(byId, m);
      changed = true;
    }
  }
}

// ── Public: build a playable double-elim tournament from a slot array ─────────
export function buildDoubleFromSlots(slots) {
  const state = buildStructure(slots.length);
  for (let s = 0; s < slots.length / 2; s++) {
    const m = state.byId[`W0-${s}`];
    m.team1 = slots[2 * s] ?? null;
    m.team2 = slots[2 * s + 1] ?? null;
  }
  resolveByes(state);
  return state;
}

export function createDoubleBracket(names) {
  return buildDoubleFromSlots(randomSlots(names));
}

export function createSeededDoubleBracket(rankedPlayers, allNames) {
  return buildDoubleFromSlots(seededSlots(rankedPlayers, allNames));
}

// ── Serialize / hydrate for persistence (resume) ─────────────────────────────
// byId holds the same object references as matches, so we don't store it — we
// rebuild it on load. Layout (x/y) is already in the match objects.
export function serializeDouble(state) {
  return {
    matches: state.matches,
    size: state.size,
    k: state.k,
    numLb: state.numLb,
  };
}

export function hydrateDouble(de) {
  const byId = {};
  de.matches.forEach((m) => (byId[m.id] = m));
  return { matches: de.matches, byId, size: de.size, k: de.k, numLb: de.numLb };
}

// ── Clone (for immutable React updates) ──────────────────────────────────────
function cloneState(state) {
  const matches = state.matches.map((m) => ({ ...m }));
  const byId = {};
  for (const m of matches) byId[m.id] = m;
  return { ...state, matches, byId };
}

// ── Advance: the user picks a winner for a (real) match ──────────────────────
export function advanceDouble(state, id, winnerName, winnerScore, loserScore) {
  const next = cloneState(state);
  const m = next.byId[id];
  if (!m || m.decided) return next;
  if (m.team1 == null || m.team2 == null) return next; // not a real, playable match

  const loser = m.team1 === winnerName ? m.team2 : m.team1;
  m.decided = true;
  m.winner = winnerName;
  m.loser = loser;
  // Store scores in team-position order on the match.
  if (m.team1 === winnerName) {
    m.s1 = winnerScore ?? null;
    m.s2 = loserScore ?? null;
  } else {
    m.s1 = loserScore ?? null;
    m.s2 = winnerScore ?? null;
  }

  if (id === "GF") {
    // Grand final: team1 = winners-bracket champ, team2 = losers-bracket champ.
    if (winnerName !== m.team1) {
      // The LB champ won → both players now have one loss → bracket reset.
      const gfr = next.byId["GFR"];
      gfr.team1 = m.team1;
      gfr.team2 = m.team2;
    }
    // If the WB champ won, the tournament is over (no reset).
  } else if (id === "GFR") {
    // Reset game — winner is the champion; nothing to propagate.
  } else {
    propagate(next.byId, m);
    resolveByes(next);
  }
  return next;
}

// The winners-bracket champion is whoever won the WB final — identified from
// the match itself, so this works for both live and reconstructed states
// (reconstruction fills GF slots in loser/winner order, not by source).
function wbChampion(state) {
  return state.byId[`W${state.k - 1}-0`]?.winner ?? null;
}

// ── Champion / runner-up ─────────────────────────────────────────────────────
export function getChampion(state) {
  const gf = state.byId["GF"];
  const gfr = state.byId["GFR"];
  if (gfr && gfr.decided) return gfr.winner; // reset decided it
  if (gf && gf.decided && gf.winner && gf.winner === wbChampion(state))
    return gf.winner; // WB champ won the grand final outright
  return null;
}

export function getRunnerUp(state) {
  if (!getChampion(state)) return null;
  const gfr = state.byId["GFR"];
  const gf = state.byId["GF"];
  if (gfr && gfr.decided) return gfr.loser ?? null;
  return gf?.loser ?? null;
}

// Is the reset game in play / relevant? (GF decided and the LB champ won it.)
export function resetActive(state) {
  const gf = state.byId["GF"];
  return !!(gf && gf.decided && gf.winner && gf.winner !== wbChampion(state));
}

// ── Encode / decode a match's persisted (roundNumber, matchIndex) ─────────────
// Winners rounds use 0..; losers rounds use 1000+round; grand final 2000 / 2001.
// This keeps double-elim results in the existing MatchResult schema (no DB change)
// and lets the archive detect "this is a double-elim tournament".
export function encodeMatch(m) {
  if (m.bracket === "W") return { roundNumber: m.round, matchIndex: m.slot };
  if (m.bracket === "L")
    return { roundNumber: 1000 + m.round, matchIndex: m.slot };
  return { roundNumber: m.id === "GFR" ? 2001 : 2000, matchIndex: 0 };
}

function matchIdFor(roundNumber, matchIndex) {
  if (roundNumber >= 2000) return roundNumber === 2001 ? "GFR" : "GF";
  if (roundNumber >= 1000) return `L${roundNumber - 1000}-${matchIndex}`;
  return `W${roundNumber}-${matchIndex}`;
}

export function isDoubleResults(results) {
  return (results || []).some((r) => r.roundNumber >= 1000);
}

// Recover the original winners-bracket round-0 seeding (player names + byes)
// from the recorded results. Real round-1 matches that were never recorded are
// byes; the bye player is inferred from the round-1 match they advanced into.
function reconstructSlots(results, S) {
  const half = S / 2;
  const slots = new Array(S).fill(undefined);

  const r0 = {}; // matchIndex -> { winner, loser }  (real WB round-0 games)
  const r1 = {}; // matchIndex -> { a, b }           (WB round-1 participants)
  for (const res of results) {
    if (res.roundNumber === 0)
      r0[res.matchIndex] = { winner: res.winnerName, loser: res.loserName };
    if (res.roundNumber === 1)
      r1[res.matchIndex] = { a: res.winnerName, b: res.loserName };
  }

  // Real round-0 matches: place both players (order within the pair is cosmetic).
  for (let m = 0; m < half; m++) {
    if (r0[m]) {
      slots[2 * m] = r0[m].winner;
      slots[2 * m + 1] = r0[m].loser;
    }
  }

  // Bye round-0 matches (no recorded game): infer the lone player.
  for (let m = 0; m < half; m++) {
    if (r0[m] || half === 1) continue;
    const par = r1[m >> 1];
    let byePlayer = null;
    if (par) {
      const sibling = r0[m ^ 1];
      if (sibling) {
        // round-1 players are the winners of the two children → the bye player
        // is whichever round-1 participant isn't the sibling's winner.
        byePlayer = par.a === sibling.winner ? par.b : par.a;
      } else {
        // both children were byes → split the two players by child parity.
        byePlayer = m % 2 === 0 ? par.a : par.b;
      }
    }
    slots[2 * m] = byePlayer ?? null;
    slots[2 * m + 1] = null;
  }

  for (let i = 0; i < S; i++) if (slots[i] === undefined) slots[i] = null;
  return slots;
}

// ── Rebuild an archived double-elim bracket from recorded results ─────────────
// We recover the seeding, build a fresh tournament (so byes auto-resolve and
// render just like the live bracket), then replay every recorded result.
export function rebuildDoubleFromResults(results, totalPlayers) {
  if (!results || results.length === 0) return null;
  const size = nextPow2(totalPlayers);

  let state = buildDoubleFromSlots(reconstructSlots(results, size));

  // Replay results in dependency order: apply any whose match is ready, repeat.
  let remaining = results.map((r) => ({
    id: matchIdFor(r.roundNumber, r.matchIndex),
    winnerName: r.winnerName,
    winnerScore: r.winnerScore ?? null,
    loserScore: r.loserScore ?? null,
  }));
  let progress = true;
  while (remaining.length && progress) {
    progress = false;
    const still = [];
    for (const res of remaining) {
      const m = state.byId[res.id];
      if (
        m &&
        !m.decided &&
        typeof m.team1 === "string" &&
        typeof m.team2 === "string"
      ) {
        state = advanceDouble(
          state,
          res.id,
          res.winnerName,
          res.winnerScore,
          res.loserScore,
        );
        progress = true;
      } else {
        still.push(res);
      }
    }
    remaining = still;
  }

  return state;
}
